/**
 * Activity's UCP checkout view (founder decision 2026-10-23) — the UNSIGNED
 * lifecycle stream for sessions a platform opens against this merchant's
 * UCP endpoint. Rendered in place of the per-agent visit groups when the
 * list's protocol chip is on `ucp`, so a merchant can see sessions BEFORE
 * payment: created, items edited, buyer added, ready to complete, canceled
 * or abandoned. Only the `completed` row carries a signed audit record.
 *
 * Reads `GET /v1/sites/:site_id/ucp-sessions`; the detail panel reads
 * `GET /v1/sites/:site_id/ucp-sessions/:session_id` via `UcpSessionDetail`.
 */

import { useEffect, useState } from 'react';

import type { SiteId } from '@sill/types';
import type { UcpPlatformGroup, UcpSessionRow, UcpSessionsListResponse } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { Card, RelativeTime } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface UcpSessionsListProps {
  readonly siteId: SiteId;
  readonly selectedSessionId: string | null;
  readonly onSelect: (session: UcpSessionRow, group: UcpPlatformGroup) => void;
}

/** List grouped by platform host. One block per platform; sessions inline. */
export function UcpSessionsList({ siteId, selectedSessionId, onSelect }: UcpSessionsListProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const [data, setData] = useState<UcpSessionsListResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const list = api.mandates.listUcpSessions;
    if (list === undefined) {
      setFailed(true);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    void list
      .call(api.mandates, siteId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, siteId]);

  if (failed) {
    return (
      <Card className="p-4 sm:p-6">
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1.5 }}>
          UCP sessions could not be loaded.
        </div>
      </Card>
    );
  }
  if (loading && data === null) {
    return (
      <Card className="p-4 sm:p-6">
        <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5 }}>
          LOADING UCP SESSIONS…
        </div>
      </Card>
    );
  }
  if (data === null || data.groups.length === 0) {
    return (
      <Card className="p-4 sm:p-6">
        <div
          style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 4 }}
        >
          UCP CHECKOUT SESSIONS
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
          No UCP checkout sessions yet. A platform like Google&apos;s AI Mode will show here when it opens a
          session against this site&apos;s UCP endpoint.
        </div>
      </Card>
    );
  }

  return (
    <div data-testid="ucp-sessions-list">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 8,
        }}
      >
        {data.total_sessions.toLocaleString('en-US')} UCP SESSION
        {data.total_sessions === 1 ? '' : 'S'} · LAST {Math.round(
          (Date.parse(data.window.to) - Date.parse(data.window.from)) / 86_400_000,
        )}
        D
      </div>
      {data.groups.map((g) => (
        <PlatformBlock
          key={g.group_key}
          group={g}
          selectedSessionId={selectedSessionId}
          onSelect={(s) => onSelect(s, g)}
        />
      ))}
    </div>
  );
}

function PlatformBlock({
  group,
  selectedSessionId,
  onSelect,
}: {
  group: UcpPlatformGroup;
  selectedSessionId: string | null;
  onSelect: (s: UcpSessionRow) => void;
}): JSX.Element {
  const C = useC();
  return (
    <Card className="mb-3">
      <div
        className="p-4"
        style={{
          borderBottom: `1px solid ${C.borderSoft}`,
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
            {group.platform_host}
          </div>
          <RelativeTime
            iso={group.last_seen}
            style={{ fontFamily: monoFont, fontSize: 9, color: C.muted }}
          />
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 0.5,
            marginTop: 4,
          }}
        >
          {group.session_count} session{group.session_count === 1 ? '' : 's'}
          {group.completed_count > 0 ? ` · ${group.completed_count} completed` : ''}
          {group.canceled_count > 0 ? ` · ${group.canceled_count} canceled` : ''}
          {group.open_count > 0 ? ` · ${group.open_count} open` : ''}
          {group.expired_count > 0 ? ` · ${group.expired_count} expired` : ''}
        </div>
      </div>
      <div>
        {group.sessions.map((s, i) => (
          <SessionRow
            key={s.session_id}
            session={s}
            isSelected={s.session_id === selectedSessionId}
            isLast={i === group.sessions.length - 1}
            onSelect={() => onSelect(s)}
          />
        ))}
      </div>
    </Card>
  );
}

function SessionRow({
  session,
  isSelected,
  isLast,
  onSelect,
}: {
  session: UcpSessionRow;
  isSelected: boolean;
  isLast: boolean;
  onSelect: () => void;
}): JSX.Element {
  const C = useC();
  const label = session.expired && session.status !== 'completed' && session.status !== 'canceled'
    ? 'Expired'
    : statusLabel(session.status);
  const tone = statusTone(session, C);
  const total = formatMoney(session.cart_total_minor, session.currency ?? 'USD');
  return (
    <button
      type="button"
      onClick={onSelect}
      data-session-id={session.session_id}
      className="w-full p-3 text-left flex items-center gap-3"
      style={{
        background: isSelected ? C.surface2 : 'transparent',
        border: 'none',
        borderBottom: !isLast ? `1px solid ${C.borderSoft}` : 'none',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: tone,
          letterSpacing: 1.2,
          minWidth: 72,
        }}
      >
        {label.toUpperCase()}
      </span>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.text,
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {total} · {session.line_count} line{session.line_count === 1 ? '' : 's'}
        {session.audit_record_id !== null ? ' · signed' : ''}
      </span>
      <RelativeTime
        iso={session.updated_at}
        style={{ fontFamily: monoFont, fontSize: 9, color: C.muted }}
      />
    </button>
  );
}

export function statusLabel(status: UcpSessionRow['status']): string {
  switch (status) {
    case 'incomplete':
      return 'Incomplete';
    case 'requires_escalation':
      return 'Escalated';
    case 'ready_for_complete':
      return 'Ready';
    case 'complete_in_progress':
      return 'Completing';
    case 'completed':
      return 'Completed';
    case 'canceled':
      return 'Canceled';
    default:
      return status;
  }
}

function statusTone(session: UcpSessionRow, C: ReturnType<typeof useC>): string {
  if (session.status === 'completed') return C.limeFg;
  if (session.status === 'canceled') return C.coralFg;
  if (session.expired) return C.amberFg;
  if (session.status === 'requires_escalation') return C.amberFg;
  if (session.status === 'ready_for_complete') return C.skyFg;
  return C.muted;
}

export function formatMoney(amount_minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount_minor / 100);
  } catch {
    return `${amount_minor} ${currency}`;
  }
}
